import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Paper, Typography, Alert, Chip, Card, CardContent, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, TextField, IconButton, Collapse, TablePagination,
  CircularProgress,
} from '@mui/material';
import { Groups, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { liveKitService, liveKitTempRoomService, projectService } from '../services/api';
import { format } from 'date-fns';
import FeatureToggleSwitch from './FeatureToggleSwitch';
import SessionsDrillDown from './SessionsDrillDown';
import { usePagination } from '../hooks/usePagination';

// Admin report for the LiveKit "virtual presence" feature — independent from GoogleMeetAdmin
// (see LIVEKIT_SETUP.md). Rooms are project-scoped (2026-08-14): admin picks a project first,
// then sees that project's room/report — mirrors how ProjectManagement already scopes employee
// assignment. Simpler than GoogleMeetAdmin otherwise: no OAuth connection lifecycle, and no
// ambiguous-participant resolution (LiveKit identity mapping can never be ambiguous — see
// LiveKitPresenceSession).
const today = () => new Date().toISOString().split('T')[0];

const LiveKitAdmin: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [date, setDate] = useState(today());
  const [daily, setDaily] = useState<any>(null);
  const [report, setReport] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [msgSeverity, setMsgSeverity] = useState<'info' | 'success' | 'error'>('info');
  const [loading, setLoading] = useState(false);

  // Drill-down: which employee's row is currently expanded (only one at a time), and a cache of
  // fetched sessions per employeeId so re-expanding a row already opened this page load doesn't
  // refetch. Cleared whenever the underlying day changes (see loadDailyAndReport).
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<number | null>(null);
  const [sessionsByEmployee, setSessionsByEmployee] = useState<Record<number, any[]>>({});

  // Temporary Meetings — project-agnostic, so it's independent of the projectId/date picker
  // above; loaded once on mount and refreshed on demand.
  const [tempRooms, setTempRooms] = useState<any[]>([]);
  const [expandedTempRoomId, setExpandedTempRoomId] = useState<number | null>(null);
  const [participantsByRoom, setParticipantsByRoom] = useState<Record<number, any[]>>({});
  const [expandedParticipantKey, setExpandedParticipantKey] = useState<string | null>(null);
  const [tempSessionsByKey, setTempSessionsByKey] = useState<Record<string, any[]>>({});
  const { pagedRows: pagedTempRooms, paginationProps: tempRoomsPaginationProps } = usePagination(tempRooms);

  const loadTempRooms = useCallback(async () => {
    try {
      const rooms = await liveKitTempRoomService.getActiveRooms();
      setTempRooms(rooms || []);
    } catch {
      setTempRooms([]);
    }
  }, []);

  useEffect(() => {
    loadTempRooms();
  }, [loadTempRooms]);

  const toggleExpandTempRoom = async (roomId: number) => {
    if (expandedTempRoomId === roomId) {
      setExpandedTempRoomId(null);
      return;
    }
    setExpandedTempRoomId(roomId);
    setExpandedParticipantKey(null);
    if (!participantsByRoom[roomId]) {
      try {
        const rows = await liveKitTempRoomService.getParticipants(roomId);
        setParticipantsByRoom(prev => ({ ...prev, [roomId]: rows || [] }));
      } catch {
        setParticipantsByRoom(prev => ({ ...prev, [roomId]: [] }));
      }
    }
  };

  const toggleExpandTempParticipant = async (roomId: number, employeeId: number) => {
    const key = `${roomId}-${employeeId}`;
    if (expandedParticipantKey === key) {
      setExpandedParticipantKey(null);
      return;
    }
    setExpandedParticipantKey(key);
    if (!tempSessionsByKey[key]) {
      try {
        const sessions = await liveKitTempRoomService.getSessions(roomId, employeeId);
        setTempSessionsByKey(prev => ({ ...prev, [key]: sessions || [] }));
      } catch {
        setTempSessionsByKey(prev => ({ ...prev, [key]: [] }));
      }
    }
  };

  const showMsg = (text: string, severity: 'info' | 'success' | 'error' = 'info') => {
    setMessage(text);
    setMsgSeverity(severity);
    setTimeout(() => setMessage(''), 5000);
  };

  useEffect(() => {
    projectService.getProjects().then((data: any[]) => {
      setProjects(data);
      if (data.length > 0) setProjectId(data[0].id);
    }).catch(() => setProjects([]));
  }, []);

  const loadDailyAndReport = useCallback(async () => {
    if (!projectId) return;
    setExpandedEmployeeId(null);
    setSessionsByEmployee({});
    try {
      const day = await liveKitService.getDaily(projectId, date);
      setDaily(day);
      if (day?.id) {
        const rows = await liveKitService.getReport(day.id);
        setReport(rows || []);
      } else {
        setReport([]);
      }
    } catch {
      setDaily(null);
      setReport([]);
    }
  }, [projectId, date]);

  const toggleExpand = async (employeeId: number | null) => {
    if (!employeeId || !daily?.id) return;
    if (expandedEmployeeId === employeeId) {
      setExpandedEmployeeId(null);
      return;
    }
    setExpandedEmployeeId(employeeId);
    if (!sessionsByEmployee[employeeId]) {
      try {
        const sessions = await liveKitService.getSessions(daily.id, employeeId);
        setSessionsByEmployee(prev => ({ ...prev, [employeeId]: sessions || [] }));
      } catch {
        setSessionsByEmployee(prev => ({ ...prev, [employeeId]: [] }));
      }
    }
  };

  useEffect(() => {
    loadDailyAndReport();
  }, [loadDailyAndReport]);

  const handleCreateDaily = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await liveKitService.createDaily(projectId);
      showMsg('Today\'s virtual office room is ready.', 'success');
      loadDailyAndReport();
    } catch (error: any) {
      showMsg(error.response?.data?.message || 'Failed to create today\'s room.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const totalDurationSeconds = report.reduce((sum, r) => sum + (r.totalDurationSeconds || 0), 0);
  const { pagedRows: pagedReport, paginationProps: reportPaginationProps } = usePagination(report);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">LiveKit Virtual Office</Typography>

      {message && <Alert severity={msgSeverity} sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <FeatureToggleSwitch
        featureKey="LiveKit"
        label="Virtual Office Integration"
        description="When off, employees cannot connect and the background reconciliation job pauses. Toggling here takes effect immediately, no deploy needed."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Project</InputLabel>
          <Select value={projectId} label="Project" onChange={e => setProjectId(Number(e.target.value))}>
            {projects.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Date"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today() }}
        />
      </Stack>

      {!projectId ? (
        <Alert severity="info">No active projects to show. Create a project first.</Alert>
      ) : (
        <>
          <Card sx={{ mb: 3, maxWidth: 420 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Groups fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">
                  {date === today() ? "Today's Room" : `Room — ${format(new Date(date), 'dd MMM yyyy')}`}
                </Typography>
              </Stack>
              <Chip
                label={daily ? daily.status?.toUpperCase() : 'NOT CREATED'}
                color={daily?.status === 'Active' ? 'success' : daily ? 'warning' : 'default'}
                sx={{ mb: 1 }}
              />
              {daily ? (
                <>
                  <Typography variant="body2">Room: {daily.roomName}</Typography>
                  <Typography variant="body2">Connected right now: {daily.connectedNowCount}</Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {date === today() ? 'No room created for today yet.' : 'No room existed for this project on that date.'}
                </Typography>
              )}
              {date === today() && (
                <Box mt={2}>
                  <Button size="small" variant="contained" onClick={handleCreateDaily} disabled={loading}>
                    {daily ? 'Recheck / Create Today\'s Room' : 'Create Today\'s Room'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Presence Report {daily ? `— ${format(new Date(daily.presenceDate), 'dd MMM yyyy')}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total connected time across all employees: {Math.round(totalDurationSeconds / 60)} min
              </Typography>
            </Stack>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click a row with sessions to see individual join/leave times.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  {['Employee', 'Attendance', 'Connected Now', 'First Join', 'Last Leave', 'Sessions', 'Total Time'].map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {report.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No presence recorded yet today.</TableCell></TableRow>
                ) : pagedReport.map((row, idx) => {
                  const expandable = row.sessionCount > 0 && row.employeeId;
                  const expanded = expandable && expandedEmployeeId === row.employeeId;
                  return (
                    <React.Fragment key={idx}>
                      <TableRow hover sx={expandable ? { cursor: 'pointer' } : undefined} onClick={() => expandable && toggleExpand(row.employeeId)}>
                        <TableCell>
                          {expandable && (
                            <IconButton size="small">
                              {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{row.employeeName}</TableCell>
                        <TableCell>{row.attendanceStatus}</TableCell>
                        <TableCell>
                          {row.connected ? <Chip label="Connected" color="success" size="small" /> : '—'}
                        </TableCell>
                        <TableCell>{row.firstJoin ? format(new Date(row.firstJoin), 'HH:mm') : '—'}</TableCell>
                        <TableCell>{row.lastLeave ? format(new Date(row.lastLeave), 'HH:mm') : '—'}</TableCell>
                        <TableCell>{row.sessionCount}</TableCell>
                        <TableCell>{Math.round((row.totalDurationSeconds || 0) / 60)} min</TableCell>
                      </TableRow>
                      {expandable && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0 }}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ bgcolor: 'action.hover' }}>
                                <SessionsDrillDown sessions={expanded ? (sessionsByEmployee[row.employeeId] ?? null) : null} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {report.length > 0 && <TablePagination {...reportPaginationProps} />}
          </TableContainer>
        </>
      )}

      {/* Temporary Meetings — employee-created, project-agnostic rooms (shareable code, no
          project/date scoping) — see TempMeetingRoom.tsx. Independent of the picker above. */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 4, mb: 1 }}>Temporary Meetings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Active ad-hoc rooms employees started themselves. Click a room to see participants; click a
        participant to see their individual join/leave sessions.
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              {['Code', 'Title', 'Created By', 'Status', 'Started', 'Connected Now'].map(h => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tempRooms.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No active temporary meetings right now.</TableCell></TableRow>
            ) : pagedTempRooms.map((room) => {
              const expanded = expandedTempRoomId === room.id;
              const participants = participantsByRoom[room.id];
              return (
                <React.Fragment key={room.id}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpandTempRoom(room.id)}>
                    <TableCell>
                      <IconButton size="small">
                        {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell><Chip label={room.roomCode} size="small" /></TableCell>
                    <TableCell>{room.title || '—'}</TableCell>
                    <TableCell>{room.createdByName}</TableCell>
                    <TableCell>
                      <Chip label={room.status} color={room.status === 'Active' ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>{format(new Date(room.createdAt), 'dd MMM yyyy, HH:mm')}</TableCell>
                    <TableCell>{room.connectedNowCount}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0 }}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ bgcolor: 'action.hover', p: 1 }}>
                          {participants === undefined ? (
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={18} /></Box>
                          ) : participants.length === 0 ? (
                            <Box sx={{ p: 2, color: 'text.secondary' }}>No one has joined this room yet.</Box>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {['Employee', 'Attendance', 'Connected', 'First Join', 'Last Leave', 'Sessions', 'Total Time'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {participants.map((p) => {
                                  const key = `${room.id}-${p.employeeId}`;
                                  const participantExpanded = expandedParticipantKey === key;
                                  return (
                                    <React.Fragment key={key}>
                                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpandTempParticipant(room.id, p.employeeId)}>
                                        <TableCell>{p.employeeName}</TableCell>
                                        <TableCell>{p.attendanceStatus}</TableCell>
                                        <TableCell>{p.connected ? <Chip label="Connected" color="success" size="small" /> : '—'}</TableCell>
                                        <TableCell>{p.firstJoin ? format(new Date(p.firstJoin), 'HH:mm') : '—'}</TableCell>
                                        <TableCell>{p.lastLeave ? format(new Date(p.lastLeave), 'HH:mm') : '—'}</TableCell>
                                        <TableCell>{p.sessionCount}</TableCell>
                                        <TableCell>{Math.round((p.totalDurationSeconds || 0) / 60)} min</TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={7} sx={{ py: 0 }}>
                                          <Collapse in={participantExpanded} timeout="auto" unmountOnExit>
                                            <SessionsDrillDown sessions={participantExpanded ? (tempSessionsByKey[key] ?? null) : null} />
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {tempRooms.length > 0 && <TablePagination {...tempRoomsPaginationProps} />}
      </TableContainer>
    </Box>
  );
};

export default LiveKitAdmin;
